<template>
  <div ref="pdpRef" class="font-sans bg-white text-gray-900 product-pdp-layout pdp-wrapper">
    <div
      v-show="productData"
      class="pdp-content"
    >
    <div class="px-6 py-5 lg:p-12">
      <div
        class="m-auto max-w-(--max-w-container) xl:max-w-(--max-w-container-xl) 2xl:max-w-(--max-w-container-2xl)">
        <HeaderSection :title="heading" :breadcrumbItems="breadcrumbItems" />
        <div v-if="product?.sku" class="mt-6 flex items-center gap-4 text-[1rem] text-ash-100">
          <div v-if="isYotpoEnabled" class="yotpo-bottomline-wrapper" @click="goToReviews" role="button"
            aria-label="View product reviews" @keydown.enter="goToReviews" @keydown.space.prevent="goToReviews">
            <div class="yotpo-widget-instance" :key="`stars-${product?.sku}`"
              :data-yotpo-instance-id="String(yotpoStarsWidgetId)" :data-yotpo-product-id="product?.sku"></div>
          </div>
          <div v-else class="yotpo-bottomline-wrapper" @click="goToReviews">
            <div class="yotpo bottomLine" :key="`bottomline-${product?.sku}`" :data-product-id="product?.sku"></div>
          </div>
        </div>
      </div>
    </div>
    <hr class="border-t border-gray-300 w-full" aria-hidden="true">
    <ContentLinks :closerLookPageUrl="closerLookPageUrl"  :isStickyReady="stickyReady" :isTaiaSectionVisible="isTaiaSectionVisible" :isFaqSectionVisible="Boolean(!faqsPending && faqs.length)" />
    <div class="bg-[linear-gradient(90deg,white_50%,var(--color-gray-50)_100%)]">
      <div
        class="m-auto max-w-[68.5714rem] lg:max-w-[68.5714rem] xl:max-w-[82.2857rem] 2xl:max-w-384 grid grid-cols-1 lg:grid-cols-4">
        <div class="order-1 lg:order-1 lg:col-span-3">
          <ProductGallery 
            :product="thumbnailImages" 
            :externalMediaIds="externalMediaIds"
            :audioDescriptions="audioDescriptions" 
            :productName="product?.name" 
            :primaryImageUrl="primaryImageUrl"
            :productId="product?.id" 
          />
          <hr class="border-t-top border-t-gray-300 absolute left-0 w-full md:w-[80vw]" />

          <section class="lg:hidden" v-if="productData">
            <ProductActionPanel 
              :blogContent="articles??[]"
              :thorneVerifiedData="thorneVerifiedData"
              :product="product"
              :activeProductId="activeProductId"
              @bundle-select="onBundleSelect"
              add-to-cart-id="productPage"
            />
          </section>

          <div class="info-sections info-sections order-3 lg:order-3 mt-16 lg:mt-0">
            <section id="taiaProductOverview" class="section is-medium" v-if="isTaiaSectionVisible">
              <TaiaProductOverview
                :compare-data="compareData"
                :overview-data="overviewData"
                :loading="tgptPending"
                :defaultTab="defaultTab"
                :isOverviewVisible="isOverviewVisible"
                :isCompareVisible="isCompareVisible ?? isNotProductBundle"
                :product="product"
              ></TaiaProductOverview>
				  </section>
            <div>
              <IngredientInformation v-if="product" :product="product" />
            </div>
            <hr class="border-t-top border-t-gray-300 absolute left-0 w-full md:w-[80vw]" aria-hidden="true"></hr>
            <div>
              <Transcript id="product-info" :heading="product?.name" :description="product?.longDescription" :expanded="expandDetailTranscript"  />
            </div>
            <div>
              <WhoShouldUse v-if="whoShouldUse.length" :items="whoShouldUse" />
            </div>
            <div>
              <ContentSections v-if="contentSections.length" :sections="contentSections" />
            </div>
            <div v-if="product?.warnings">
              <Warnings :description="product?.warnings" />
            </div>
            <div class="mt-12 mb-12" v-if="!faqsPending && faqs.length">
              <FaqExpandableList :items="faqs" />
            </div>
            <div id="reviews"
                 class="py-8 px-6 md:py-16 md:pr-[2.14rem] md:pl-0 lg:scroll-mt-15 scroll-mt-35"
                 :class="{ 'pdp-content-visible':true }"
            >
              <div v-if="isYotpoEnabled && product?.sku"
                   :key="`reviews-${product.sku}`"
                   :id="`yotpo-reviews-${product.sku}`"
                   class="yotpo-widget-instance pagination-section"
                   :class="{ 'pdp-content-visible':true }"
                   :data-yotpo-instance-id="String(yotpoReviewsWidgetId)"
                   :data-yotpo-product-id="product.sku"
                   :data-yotpo-name="product.name"
                   :data-yotpo-url="product.url"
                   :data-yotpo-image-url="product.primaryImageUrl"
                   :data-yotpo-price="product.price"
                   data-yotpo-currency="USD"
                   :data-yotpo-description="product.shortDescription">
              </div>
              <div v-else-if="product?.sku"
                   :key="`main-${product.sku}`"
                   :id="`yotpo-reviews-${product.sku}`"
                   class="yotpo yotpo-main-widget"
                   :data-product-id="product.sku" :data-name="product.name"
                   :data-url="`/${URL_CONFIG.PRODUCT_BASE_URL}/${product.sku}`"
                   :data-image-url="product.primaryImageUrl"
                   :data-description="product.name">
              </div>
            </div>
          </div>
        </div>
        <div class="order-2 lg:order-2 py-[2.143rem] lg:py-12 px-0 border-l-0 md:border-l md:border-gray-300 bg-gray-200 z-10 relative">
          <BuyBox v-if="product"
            :product="product"
            :items="articles??[]"
            :thorneVerifiedData="thorneVerifiedData"
            :activeProductId="activeProductId"
            :subscriptionitems="subscriptionitems"
            @bundle-select="onBundleSelect"
            />
        </div>
      </div>
    </div>
    <section class="bg-ash-400 flex py-4 px-4 pt-[2.643rem] lg:py-32 lg:px-6">
      <div class="m-auto max-w-[68.5714rem] lg:max-w-[68.5714rem] xl:max-w-[82.2857rem] 2xl:max-w-384">
        <Heading :level="3" color="text-ash-200" class="mb-6">Recommendations for you </Heading>
        <div class="lg:grid lg:grid-cols-4">
          <!-- Desktop Grid -->
          <ProductTile class="hidden lg:block" v-for="(item, index) in crossSells" :key="item.id" 
            :id="item.id"
            :sku="item.sku" 
            :name="item.name"
            :url="item.url" 
            :price="item.price"
            :primaryImageUrl="item.imageUrls?.[0]"
            :headline="item.headline" 
            :shortDescription="item.shortDescription"
            :benefits="Array.isArray(item.benefits) ? item.benefits.join(', ') : item.benefits ?? ''"
            :tag="''"
            :reviewSummary="{ averageRating: item.reviewSummary?.averageRating ?? 0, reviewCount: item.reviewSummary?.reviewCount ?? 0 }"
            :index-value="index" 
          />
          <!-- Mobile Carousel -->
          <div class="lg:hidden">
            <Carousel ref="recommendedProductsCarousel" :total-items="crossSells?crossSells.length:0" :page-size="1" :show-slide-selector="false"
                      v-slot="{ index }">
              <!-- Previous Arrow -->
              <div class="absolute left-2 top-1/2 -translate-y-1/2 z-10">
                <button class="text-3.5xl"
                        @click="recommendedProductsCarousel?.previousSlide()" aria-label="Previous slide">
                  <Icon icon="chevron-left" />
                </button>
              </div>
              <!-- Slide Content -->
              <div class="px-2">
                <ProductTile v-for="(item, index) in crossSells" :key="item.id"
                  :id="item.id"
                  :sku="item.sku"
                  :name="item.name"
                  :url="item.url"
                  :price="item.price"
                  :primaryImageUrl="item.imageUrls?.[0]"
                  :headline="item.headline"
                  :shortDescription="item.shortDescription"
                  :benefits="Array.isArray(item.benefits) ? item.benefits.join(', ') : item.benefits ?? ''"
                  :tag="''"
                  :reviewSummary="{ averageRating: item.reviewSummary?.averageRating ?? 0, reviewCount: item.reviewSummary?.reviewCount ?? 0 }"
                  :index-value="index" 
                  class="w-full" 
                />
              </div>
              <!-- Next Arrow -->
              <div class="absolute right-2 top-1/2 -translate-y-1/2 z-10">
                <button class="text-3.5xl"
                        @click="recommendedProductsCarousel?.nextSlide()" aria-label="Next slide">
                  <Icon icon="chevron-right" />
                </button>
              </div>
            </Carousel>
          </div>
        </div>
      </div>
    </section>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from "vue"

import HeaderSection from '~/components/ui/organism/headerSection.vue'
import ContentLinks from '~/components/ui/organism/product/contentLinks.vue'
import Icon from '~/components/ui/atom/icon.vue'
import BuyBox from '~/components/ui/organism/product/buyBox.vue'
import IngredientInformation from '~/components/ui/organism/product/ingredientInformation.vue'
import Transcript from "~/components/ui/molecule/transcript.vue"
import Carousel from '~/components/ui/molecule/carousel/index.vue'
import FaqExpandableList from '~/components/ui/organism/faq/faqExpandableList.vue'
import type { BreadcrumbItem, Product } from '~~/shared/types/common'
import { convertPriceStringToFloat } from '~~/shared/utils/price'
import { URL_CONFIG } from '~~/shared/utils/url.const'
import ProductGallery from '~/components/ui/organism/product/productGallery.vue'
import ContentSections from '~/components/ui/organism/product/contentSections.vue'
import TaiaProductOverview from '~/components/ui/organism/product/taiaProductOverview.vue'
import WhoShouldUse from '~/components/ui/organism/product/whoShouldUse.vue'
import ProductTile from '~/components/ui/organism/product/productTile.vue'
import Heading from '~/components/ui/atom/heading.vue'
import Warnings from "~/components/ui/molecule/warnings.vue"
import ProductActionPanel from '~/components/ui/organism/product/productActionPanel.vue'

import { useRoute } from 'vue-router'
import { useProductFaqExpandable } from '~/composables/useProductFaq'
import { usePdpTgpt } from '~/composables/usePdpTgpt'
import { useStickyTabs } from '~/composables/useNavSticky'
import { useLocaleStore } from '~/stores/localeStore'
import { useIframeStore } from '~/stores/chatAIStore'
import { storeToRefs } from 'pinia'
import { usePdpSuggestionPills } from '~/composables/usePdpSuggestionPills'

const recommendedProductsCarousel = ref<InstanceType<typeof Carousel> | null>(null)
const iframeStore = useIframeStore()
const { isEnableTaia, chatbotReady, chatbotReadyVersion } = storeToRefs(iframeStore)

const route = useRoute()
const nuxtApp = import.meta.server ? null : useNuxtApp()
const localeStore = useLocaleStore()

const storeLocaleCode = computed(() => localeStore.locale?.localeCode)
const routeLocale = computed(() => {
  const routeQuery = route?.query?.locale
  return Array.isArray(routeQuery) ? routeQuery[0] : routeQuery
})
const resolvedLocale = computed(() =>
    (routeLocale.value ?? storeLocaleCode.value ?? 'en-US').replace('-', '_')
)
const id = ref(String(route?.params?.id ?? ''))
const stringifyLogDetails = (details: Record<string, unknown>) => {
  const seen = new WeakSet<object>()

  return JSON.stringify(details, (_key, value) => {
    if (value instanceof Error) {
      return {
        name: value.name,
        message: value.message,
        stack: value.stack,
        cause: value.cause,
      }
    }

    if (value && typeof value === 'object') {
      if (seen.has(value)) return '[Circular]'
      seen.add(value)

      if ('message' in value || 'statusCode' in value || 'statusMessage' in value || 'status' in value || 'stack' in value) {
        const errorLike = value as {
          name?: unknown
          message?: unknown
          statusCode?: unknown
          status?: unknown
          statusMessage?: unknown
          data?: unknown
          stack?: unknown
          cause?: unknown
        }

        return {
          name: errorLike.name,
          message: errorLike.message,
          statusCode: errorLike.statusCode,
          status: errorLike.status,
          statusMessage: errorLike.statusMessage,
          data: errorLike.data,
          stack: errorLike.stack,
          cause: errorLike.cause,
        }
      }
    }

    return value
  })
}

const logProductDetailPage = (message: string, details: Record<string, unknown>) => {
  console.info(`[product detail page] ${message} ${stringifyLogDetails(details)}`)
}

const logProductDetailPageError = (message: string, details: Record<string, unknown>) => {
  console.error(`[product detail page] ${message} ${stringifyLogDetails(details)}`)
}

const productAliasRequestContext = {
  alias: id.value,
  locale: resolvedLocale.value,
  routePath: route.path,
}
logProductDetailPage('fetchProductByAlias:start', productAliasRequestContext)
const { data: productData, error: productError } = await siteService().fetchProductByAlias(id.value, resolvedLocale.value)

if (productError.value) {
  logProductDetailPageError('fetchProductByAlias:error', {
    ...productAliasRequestContext,
    error: productError.value,
  })
  throw createError({
    statusCode: productError.value.statusCode ?? productError.value.status ?? 500,
    statusMessage: productError.value.statusMessage ?? 'Unable to load product'
  })
}

if (!productData.value) {
  logProductDetailPageError('fetchProductByAlias:notFound', productAliasRequestContext)
  throw createError({
    statusCode: 404,
    statusMessage: 'Page Not Found'
  })
}

logProductDetailPage('fetchProductByAlias:complete', {
  ...productAliasRequestContext,
  productId: productData.value.id,
  sku: productData.value.sku,
  isBundle: productData.value.bundle,
})

const { data: crossSellsData } = await siteService().fetchProductCrossSells(id.value)
const { data: articlesData } = await siteService().fetchProductArticles(id.value)
const {
  expandedSections,
  activeSection
} = useStickyTabs();
const product = computed<Product | undefined>(() => productData.value)
const activeProductId = computed(() => product.value?.id ?? 0)

const articles = computed<FeaturedContent[]>(() => articlesData.value ?? [])
const crossSells = computed<Product[]>(() => crossSellsData.value ?? [])
const isNotProductBundle  = computed(() => (!productData.value?.bundle))
const {
  compareData,
  overviewData,
  tgptPending,
  tgptError,
  isTaiaSectionVisible,
  defaultTab,
  isOverviewVisible,
  isCompareVisible } = usePdpTgpt(computed(() => productData.value?.sku), isNotProductBundle)

const expandDetailTranscript = computed(() => {
  return expandedSections.value['transcript-product-infosection']
})

const storeProductInfo = () => {
  const item = product?.value
  if (import.meta.client) {
    localStorage.setItem('pdpProduct', JSON.stringify({
      id: item?.id ?? null,
      sku: item?.sku ?? '',
      productName: item?.name ?? '',
      benefits: item?.benefits ?? [],
    }))
  }
}

const renderComplete = ref(false);
watch(
  () => productData.value,
  async (currentProduct) => {
    if (!import.meta.client) return

    if (!currentProduct) {
      renderComplete.value = false
      return
    }

    await nextTick();
    await new Promise<void>((r) => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => r());
      });
    });
    if (isYotpoEnabled.value) {
      await initYotpoWhenReady();
    }
    renderComplete.value = true;
    storeProductInfo()
  },
  { immediate: true }
)

const config = useRuntimeConfig()

const yotpoEnabled = computed(() => config.public.yotpoEnabled)
const yotpoStarsWidgetId = computed(() => config.public.yotpoStarsWidgetId)
const yotpoReviewsWidgetId = computed(() => config.public.yotpoReviewsWidgetId)

const isYotpoEnabled = computed(() => {
  const enabled = yotpoEnabled.value
  return Boolean(enabled === true || (enabled as any) === 'true')
})

const heading = computed(() => product.value?.name || "")
const pdpRef = ref<HTMLElement | null>(null);

watch(
    () => product.value?.url,
    (newUrl, oldUrl) => {
        if (!import.meta.client) return

        if (newUrl && newUrl !== oldUrl && newUrl !== window.location.pathname) {
            window.history.pushState({}, '', newUrl)
        }
    }
)

const thumbnailImages = computed(() => product.value?.imageUrls ?? []);
const externalMediaIds = computed(() => product.value?.externalMediaIds ?? []);
const audioDescriptions = computed(() => product.value?.audioDescriptions ?? {});
const primaryImageUrl = computed(() => product.value?.primaryImageUrl);
const contentSections = computed(() => productData.value?.contentSections ?? [])
const whoShouldUse = computed(() => product.value?.whoShouldUse ?? []);
const stickyReady = computed(() => {
  return Boolean(product.value?.sku) && renderComplete.value
})

const thorneVerifiedData = computed(() => {
  const concerns = product.value?.ingredientConcerns
  if (!concerns) return []
  return Object.entries(concerns)
    .filter(([label, links]) => Array.isArray(links) && links.length > 0)
    .map(([label, links]) => ({
      label,
      link: links[0]!,
      icon: certificationIcon(label)
    }))
})

const certificationIcon = (certification: string) => {
  const defaultIcon = 'flask-round-potion'
  let icon = ''
  if (certification.includes('Gluten')) icon = 'wheat'
  else if (certification.includes('Dairy')) icon = 'cow'
  else if (certification.includes('Soy')) icon = defaultIcon
  else if (certification.includes('Color') || certification.includes('Flavor')) icon = 'fill'
  else if (certification.includes('Vegan')) icon = 'rabbit'
  else icon = defaultIcon
  return icon
}

const breadcrumbItems = computed((): BreadcrumbItem[] => {
  const items: BreadcrumbItem[] = [
    { label: 'Home', href: '/' },
    { label: 'Products', href: `${URL_CONFIG.PRODUCT_BASE_URL}` },
  ]
  if (product.value?.sku) {
    items.push({ label: `SKU ${product.value.sku}` })
  }
  return items
})

const onBundleSelect = (variant: any) => {
  if (!variant?.productUrl) return
  navigateTo(`${variant.productUrl}`, { external: true })
}

const closerLookPageUrl = computed<string | undefined>(() => {
  const url = product.value?.closerLookPageUrl
  if (Array.isArray(url)) {
    return url[0]
  }
  return url && url !== 'none' ? url : undefined
})

const sku = computed(() => product.value?.sku ?? '')

const {
  pills: pdpSuggestionPills,
  pending: pdpSuggestionPillsPending,
} = usePdpSuggestionPills(sku)
const sentChatbarPillsKey = ref<string>('')
const sendChatbarPills = (nextSku: string, pills: string[]) => {
  iframeStore.sendMessageToChatbot('chatbarPills', {
    currentMinimise: true,
    actionType: 'pdp',
    productSku: nextSku,
    pills,
    appendPills: true,
  })
}

watch([sku, pdpSuggestionPills, pdpSuggestionPillsPending, isEnableTaia, chatbotReady, chatbotReadyVersion], ([nextSku, pills, isPending, taiaEnabled, isChatbotReady, readyVersion]) => {
  if (!import.meta.client || isPending || !taiaEnabled || !isChatbotReady || !nextSku || !Array.isArray(pills) || pills.length === 0) {
    return
  }

  const deliveryKey = `${nextSku}:${readyVersion}`
  if (sentChatbarPillsKey.value === deliveryKey) {
    return
  }

  sendChatbarPills(nextSku, pills)

  sentChatbarPillsKey.value = deliveryKey
}, { immediate: true })

const { faqs, faqsPending } = useProductFaqExpandable(sku)
const subscriptionitems = computed(() => {
  const subItemvalue = product.value as any
  return {
    discountable: Boolean(subItemvalue.discountable),
    isBloodDraw: Boolean(subItemvalue.bloodDraw),
    isBioAge: Boolean(subItemvalue.test && subItemvalue.sku === 'LAB001'),
    restrictedLocations: subItemvalue.restrictedLocations,
    hsaFsaAccepted: subItemvalue.hsaFsaAccepted
  }
})

const goToReviews = (event: Event) => {
  const sku = product.value?.sku
  const currentTarget = event.currentTarget as Element
  if (currentTarget.querySelector('.yotpo-star-rating-reviews-summary-text') === null) {
    const reviewSection = document.getElementById(`yotpo-reviews-${sku}`)
    if (reviewSection) {
      reviewSection.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }
}

const YOTPO_READY_MAX_MS = 2500

const initYotpoWhenReady = (): Promise<void> => {
  const sku = product.value?.sku
  if (!sku || typeof window === 'undefined') return Promise.resolve()

  return new Promise((resolve) => {
    let settled = false
    const done = () => {
      if (!settled) {
        settled = true
        resolve()
      }
    }

    const timeoutId = setTimeout(done, YOTPO_READY_MAX_MS)

    const runInit = () => {
      const win = window as any
      if (typeof win.yotpoWidgetsContainer?.initWidgets === 'function') {
        win.yotpoWidgetsContainer.initWidgets()
      }
      if (typeof win.yotpo?.initWidgets === 'function') {
        win.yotpo.initWidgets()
      }
    }

    const delayedRunInit = () => setTimeout(runInit, 100)
    const rafRunInit = () => requestAnimationFrame(delayedRunInit)
    const scheduleRunInit = () => nextTick(rafRunInit)

    const scheduleCheckReady = (deadline: number) => {
      const checkReady = () => {
        if (settled) return
        const starsEl = document.querySelector('.yotpo-star-rating-reviews-summary-text')
        const widgetEl = document.querySelector(`[data-yotpo-product-id="${sku}"]`)
        if (starsEl ?? widgetEl ?? Date.now() >= deadline) {
          clearTimeout(timeoutId)
          done()
          return
        }
        requestAnimationFrame(checkReady)
      }
      requestAnimationFrame(() => setTimeout(checkReady, 300))
    }

    const tryInit = (attempts = 0) => {
      const win = window as any
      const hasNew = typeof win.yotpoWidgetsContainer?.initWidgets === 'function'
      const hasLegacy = typeof win.yotpo?.initWidgets === 'function'

      if (hasNew || hasLegacy) {
        scheduleRunInit()
        const deadline = Date.now() + YOTPO_READY_MAX_MS
        scheduleCheckReady(deadline)
      } else if (attempts < 40) {
        setTimeout(() => tryInit(attempts + 1), 250)
      } else {
        clearTimeout(timeoutId)
        done()
      }
    }

    const delayedTryInit = () => setTimeout(tryInit, 200)
    const rafTryInit = () => requestAnimationFrame(delayedTryInit)
    const scheduleTryInit = () => nextTick(rafTryInit)
    scheduleTryInit()
  })
}

watch(
  () => productData.value?.sku,
  (sku) => {
    if (sku) initYotpoWhenReady()
  },
  { immediate: true, flush: 'post' }
)

// region - Search Engine Optimization (SEO)
const SCHEMA_US_REGIONS = [
  'AL', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 'ID', 'IL', 'IN', 'IA', 'KS',
  'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT',
  'VT', 'VA', 'WA', 'WV', 'WI', 'WY', 'DC'
]

/**
 * Builds Product structured data from the currently loaded PDP product.
 *
 * @returns A Schema.org Product object when product data is available; otherwise null.
 */
const productSchemaOrg = () => {
  const currentProduct = product.value as any
  if (!currentProduct?.name) return null

  const parsedPrice = convertPriceStringToFloat(currentProduct.retailPrice)
  const parsedRating = Number(currentProduct?.reviewSummary?.averageRating)
  const parsedReviewCount = Number(currentProduct?.reviewSummary?.reviewCount)

  const siteBaseUrl = String(config.public?.thorneSite ?? 'https://www.thorne.com').replace(/\/$/, '')
  const productUrl = String(currentProduct.url ?? '')
  const offerUrl = productUrl
      ? (productUrl.startsWith('http') ? productUrl : `${siteBaseUrl}${productUrl.startsWith('/') ? '' : '/'}${productUrl}`)
      : null;

  return {
    '@context': 'https://schema.org/',
    '@type': 'Product',
    name: currentProduct.name,
    image: [
      primaryImageUrl.value ?? currentProduct.imageUrls?.[0]
    ].filter(Boolean),
    description: currentProduct.shortDescription || currentProduct.headline,
    brand: {
      '@type': 'Brand',
      name: 'Thorne'
    },
    sku: currentProduct.sku || 'SF903',
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: Number.isFinite(parsedRating) ? parsedRating : null,
      reviewCount: Number.isFinite(parsedReviewCount) ? parsedReviewCount : null
    },
    offers: {
      '@type': 'Offer',
      url: offerUrl,
      price: Number.isFinite(parsedPrice) ? parsedPrice : null,
      priceCurrency: 'USD',
      availability: currentProduct.outOfStock ? 'https://schema.org/OutOfStock' : 'https://schema.org/InStock',
      itemCondition: 'https://schema.org/NewCondition',
      shippingDetails: {
        '@type': 'OfferShippingDetails',
        shippingRate: {
          '@type': 'MonetaryAmount',
          value: 0,
          currency: 'USD'
        },
        shippingDestination: {
          '@type': 'DefinedRegion',
          addressCountry: 'US',
          addressRegion: SCHEMA_US_REGIONS
        },
        deliveryTime: {
          '@type': 'ShippingDeliveryTime',
          handlingTime: {
            '@type': 'QuantitativeValue',
            minValue: 0,
            maxValue: 1,
            unitCode: 'DAY'
          },
          transitTime: {
            '@type': 'QuantitativeValue',
            minValue: 3,
            maxValue: 7,
            unitCode: 'DAY'
          }
        }
      },
      hasMerchantReturnPolicy: {
        '@type': 'MerchantReturnPolicy',
        applicableCountry: 'US',
        returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
        merchantReturnDays: 60,
        returnMethod: 'https://schema.org/ReturnByMail',
        returnFees: 'https://schema.org/RestockingFees'
      }
    }
  }
};

/**
 * Converts the full Product schema object into the node shape expected by defineProduct.
 *
 * @returns Product schema properties without @context and @type, or null when no schema can be built.
 */
const productSchemaOrgNode = () => {
  const schema = productSchemaOrg()
  if (!schema) return null
  const node = { ...schema } as Record<string, any>
  delete node['@context']
  delete node['@type']
  return node
};

/**
 * Removes HTML tags and normalizes whitespace for text used in meta tags.
 *
 * @param value - Optional HTML or plain-text source value.
 * @returns Plain text with tags removed, or an empty string when no value is provided.
 */
const stripHtml = (value?: string | null) =>
    value
        ?.replace(/<[^>]*>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim() ?? ''

/**
 * Resolves the PDP document title from the product name.
 *
 * @returns A computed page title suitable for the document head.
 */
const productMetaTitle = computed(() =>
    product.value?.name ? `${product.value.name} & Reviews | Thorne` : 'Thorne'
)

/**
 * Resolves the PDP meta description from product-specific copy.
 *
 * @returns A computed description using short description, headline, stripped long description, or a product-name fallback.
 */
const productMetaDescription = computed(() => {
  const productName = product.value?.name?.trim()

  return (
      product.value?.shortDescription?.trim() ||
      product.value?.headline?.trim() ||
      stripHtml(product.value?.longDescription).trim() ||
      (productName ? `Shop ${productName} from Thorne.` : '')
  )
})

/**
 * Registers Product structured data for search engines when PDP product data is available.
 *
 * @returns An array containing the Product schema node, or an empty array until product data exists.
 */
useSchemaOrg(computed(() => {
  const schemaData = productSchemaOrgNode();
  if (!schemaData) {
    console.log("schema - No schema response");
    return []
  }
  console.log('schema product:', schemaData)
  return [defineProduct(schemaData)];
}))

/**
 * Registers PDP title and description tags in the document head.
 *
 * @returns A reactive Nuxt head object containing title and meta description entries.
 */
useHead(() => ({
  title: productMetaTitle.value,
  meta: [
    {
      name: 'description',
      content: productMetaDescription.value
    }
  ]
}))

// endregion

// region - Clickstream

/**
 * Tracks a product_viewed clickstream event for Hightouch.
 *
 * The event is scheduled as a microtask so analytics work stays fire-and-forget
 * and cannot block the rest of the onMounted lifecycle tasks. Errors are caught
 * locally to prevent clickstream failures from affecting page behavior.
 */
const trackClickstreamViewedEvent = (currentProduct = product.value) => {
  if (import.meta.server || !currentProduct?.sku) return

  const hightouchEvents = nuxtApp?.$hightouchEvents

  queueMicrotask(() => {
    try {
      hightouchEvents?.track('product_viewed', {
        product_id: String(currentProduct.id),
        sku: currentProduct.sku,
        product_name: currentProduct.name,
        price: convertPriceStringToFloat(currentProduct.retailPrice),
        currency: 'USD',
        source: 'nuxt'
      })
    } catch (error) {
      console.warn('Failed to track product_viewed clickstream event', error)
    }
  })
}

/**
 * Tracks which product SKUs have already emitted a product_viewed event during
 * this PDP component instance. The watcher can run multiple times as async
 * product data resolves or updates, so this prevents duplicate clickstream
 * events while still allowing one event for each viewed variant SKU.
 */
const trackedProductViewedSkus = new Set<string>()
watch(
  () => product.value,
  (currentProduct) => {
    const sku = currentProduct?.sku
    if (!sku || trackedProductViewedSkus.has(sku)) return

    trackedProductViewedSkus.add(sku)
    trackClickstreamViewedEvent(currentProduct)
  },
  { immediate: true }
)
// endregion

// region - Vue Lifecycle
onMounted(async() => {
  if (product.value?.sku) initYotpoWhenReady()
  let attempts = 0
  const interval = setInterval(() => {
    attempts++
    if (product.value?.sku) {
      initYotpoWhenReady()
      clearInterval(interval)
    } else if (attempts >= 25) {
      clearInterval(interval)
    }
  }, 400)
  activeSection.value = ''
  if ('scrollRestoration' in history) {
    history.scrollRestoration = 'manual'
  }
  await nextTick()
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      document.documentElement.scrollTop = 0
      document.body.scrollTop = 0
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'auto'
      })
    })
  })
})
onUnmounted(() => {
  if ('scrollRestoration' in history) {
    history.scrollRestoration = 'auto'
  }
})
// endregion

</script>

<style scoped>
.pdp-wrapper {
  position: relative;
  min-height: 90vh;
}
</style>
<style>
.pagination-section .yotpo-reviews-pagination-container .yotpo-pagination-number-container {
  @apply flex items-center;
}

.pagination-section .yotpo-reviews-main-widget .yotpo-main-widget-layout .yotpo-base-layout .yotpo-main-layout .yotpo-filters-container .yotpo-filters-container-inner .yotpo-search-filter .yotpo-search-input  {
  @apply rounded-full ;
}
.yotpo-bottomline-wrapper{
  min-height: 35px;
  height: 35px;
  max-height: 35px;
}
</style>
